import { useState } from 'react'
import { Wrench, ArrowRight, Loader2, Send, Info, ChevronDown, ChevronRight } from 'lucide-react'
import { dealsApi } from '@api/client'
import { useAuthStore } from '@store/authStore'
import { showToast } from '@store/uiStore'
import { formatCurrency } from '@utils/format'

interface InjectImplFeeWidgetProps {
  dealId: number
  /** Scope's persisted calculated implementation fee (scopes.total_cost) — exactly what the server pushes. */
  scopeCalculatedFee: number
  /** Quote's current implementation_fee. */
  quoteCurrentFee: number
  customerName: string
  /** deals.impl_review_requested_by — used for the §3.9 self-inject soft-prevent. */
  submitterEmail?: string | null
  submitterName?: string | null
  /** Called after a successful push so the parent re-fetches the deal (status → 'reviewed'). */
  onInjected: () => void
}

/**
 * Impl-review flow (PR 4) — the Implementations team's inject affordance on the scope page.
 * Shown only while the deal is 'awaiting'. Allowlist users (canEditScopeHours) get the push
 * action; everyone else gets a read-only info panel (design §3.6). Pushing requires a
 * populated scope (calculated fee > 0); the server is authoritative and re-checks (§0).
 */
export function InjectImplFeeWidget({
  dealId,
  scopeCalculatedFee,
  quoteCurrentFee,
  customerName,
  submitterEmail,
  submitterName,
  onInjected,
}: InjectImplFeeWidgetProps) {
  const { user } = useAuthStore()
  const canInject = !!user?.permissions?.canEditScopeHours
  // §3.9 — soft-prevent self-inject: warn but allow when the requester injects their own deal.
  const isSubmitter = !!user?.email && !!submitterEmail && user.email === submitterEmail
  const [busy, setBusy] = useState(false)
  const [expanded, setExpanded] = useState(false)

  // Read-only info panel for non-allowlist users (§3.6) — so an AE who opens the scope page
  // out of curiosity understands what's happening and that they don't need to act here.
  if (!canInject) {
    return (
      <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-amber-500/5 transition-colors"
        >
          {expanded ? <ChevronDown size={14} className="text-amber-400" /> : <ChevronRight size={14} className="text-amber-400" />}
          <Info size={16} className="text-amber-400 flex-shrink-0" />
          <span className="text-sm font-medium text-[var(--text-primary)]">Awaiting Implementations review</span>
        </button>
        {expanded && (
          <div className="px-4 pb-3">
            <p className="text-xs text-[var(--text-muted)] ml-8">
              Alitia or Aju will push the implementation fee for <strong>{customerName}</strong>{' '}
              once they're done with the scope. You don't need to do anything from this page.
            </p>
          </div>
        )}
      </div>
    )
  }

  const delta = scopeCalculatedFee - quoteCurrentFee
  const deltaSign = delta > 0 ? '+' : ''
  const deltaColour = delta > 0 ? 'text-amber-400' : delta < 0 ? 'text-emerald-400' : 'text-[var(--text-muted)]'
  const noScopeFee = scopeCalculatedFee <= 0

  const handlePush = async () => {
    setBusy(true)
    try {
      const res = await dealsApi.injectImplFee(dealId, {
        quoteUrl: `${window.location.origin}/quote?session=${dealId}`,
        scopeUrl: `${window.location.origin}/scope?session=${dealId}`,
      })
      if (!res.success || !res.data) {
        showToast(res.error || 'Failed to push the implementation fee.', 'error')
        return
      }
      const { before, after, slack_notification_status } = res.data
      showToast(
        `Implementation fee pushed: ${formatCurrency(before)} → ${formatCurrency(after)}`,
        'success',
        6000,
      )
      if (slack_notification_status === 'failed') {
        showToast(
          `The fee was saved, but the Slack notification to ${submitterName || 'the submitter'} didn't go out.`,
          'warning',
          8000,
        )
      }
      onInjected()
    } catch (e) {
      console.error('Inject impl fee error:', e)
      showToast('Failed to push the implementation fee. Please try again.', 'error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`rounded-lg border overflow-hidden ${isSubmitter ? 'border-amber-500/60 bg-amber-500/10' : 'border-amber-500/30 bg-amber-500/5'}`}>
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left hover:bg-amber-500/5 transition-colors"
      >
        {expanded ? <ChevronDown size={14} className="text-amber-400" /> : <ChevronRight size={14} className="text-amber-400" />}
        <Wrench size={16} className="text-amber-400 flex-shrink-0" />
        <span className="text-sm font-medium text-[var(--text-primary)]">Implementations review — push the calculated fee into the quote</span>
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          <div className="ml-8">
            <p className="text-xs text-[var(--text-muted)]">
              Review the scope hours for <strong>{customerName}</strong>, then push the calculated
              implementation fee into the quote. The submitter is notified in Slack and can continue
              to deal review.
            </p>

            <div className="mt-3 flex items-center gap-3 px-3 py-2 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-md">
              <div>
                <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Quote (current)</div>
                <div className="text-sm text-[var(--text-secondary)]">{formatCurrency(quoteCurrentFee)}</div>
              </div>
              <ArrowRight size={14} className="text-[var(--text-muted)]" />
              <div>
                <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wider">Scope (calculated)</div>
                <div className="text-sm font-semibold text-[var(--text-primary)]">{formatCurrency(scopeCalculatedFee)}</div>
              </div>
              {!noScopeFee && (
                <div className={`ml-auto text-sm font-medium ${deltaColour}`}>
                  {deltaSign}{formatCurrency(delta)}
                </div>
              )}
            </div>

            {noScopeFee && (
              <p className="text-[11px] text-amber-400 mt-2">
                Add scope hours first — the calculated fee is $0, so there's nothing to push yet.
              </p>
            )}
            {isSubmitter && (
              <p className="text-[11px] text-amber-400 mt-2">
                You requested this review — please ask Alitia or Aju to push the fee where possible.
              </p>
            )}

            <div className="mt-3">
              <button
                onClick={handlePush}
                disabled={busy || noScopeFee}
                title={noScopeFee ? 'Add scope hours before pushing — the calculated fee is $0.' : undefined}
                className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-500/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                Push calculated fee to quote
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
